import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '../components/ui/table';
import {
  MessageCircle, Loader2, Plus, X, Info, AlertTriangle, Search, RefreshCw, ArrowLeft,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  COMMON, COMMON_DETAILED, OPTIONS, COMMON_TEMPLATE_INFO,
  LEGACY_TRIGGERS, SYSTEM_PLACEHOLDERS, labelForTrigger,
} from '../constants/whatsapp';

// Every trigger a campaign can have, in the order the picker shows them — the three normal
// options first, then the older per-trigger messages (still functional, just no longer the
// default setup path — see WhatsAppTemplates.js).
const ALL_TRIGGER_KEYS = [...OPTIONS.map((o) => o.key), ...Object.keys(LEGACY_TRIGGERS)];

const blankDraft = () => ({
  template_name: '',
  body_values: [],
  language_code: 'en',
  country_code: '91',
  is_active: false,
});

const WhatsAppTemplatesManager = () => {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();

  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [configured, setConfigured] = useState(true);
  const [campaigns, setCampaigns] = useState([]); // [{campaign_id, campaign_name, whatsapp_enabled, templates:[{trigger,is_active,template_name,effective_template_name,body_values}]}]
  const [trigger, setTrigger] = useState(COMMON);
  const [draft, setDraft] = useState(blankDraft());
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set()); // campaign_ids this apply will target

  useEffect(() => {
    if (!isSuperAdmin()) {
      toast.error('Only Super Admin can manage integrations.');
      navigate('/dashboard');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchOverview = async () => {
    setLoading(true);
    try {
      const res = await api.get('/whatsapp-templates/overview');
      setConfigured(res.data?.configured !== false);
      const rows = res.data?.campaigns || [];
      setCampaigns(rows);
      setSelected(new Set(rows.map((c) => c.campaign_id)));
    } catch (err) {
      toast.error('Failed to load WhatsApp templates');
    } finally {
      setLoading(false);
    }
  };

  // Every campaign's row for the currently-picked trigger, with a quick lookup by id.
  const rowsForTrigger = useMemo(
    () => campaigns.map((c) => ({
      ...c,
      current: c.templates.find((t) => t.trigger === trigger) || null,
    })),
    [campaigns, trigger],
  );

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rowsForTrigger;
    return rowsForTrigger.filter((r) => r.campaign_name.toLowerCase().includes(q));
  }, [rowsForTrigger, search]);

  const isCommon = trigger === COMMON || trigger === COMMON_DETAILED;

  // A common trigger's approved name is the same for every campaign — read it off any row
  // that already resolved one, rather than re-deriving server config client-side.
  const commonEffectiveName = useMemo(() => {
    const hit = rowsForTrigger.find((r) => r.current?.effective_template_name);
    return hit?.current?.effective_template_name || '';
  }, [rowsForTrigger]);

  // Switching trigger starts a fresh draft — each trigger's config is independent, and the
  // active state visible per row already shows what's live so there's nothing to "restore".
  const handleTriggerChange = (key) => {
    setTrigger(key);
    setDraft(blankDraft());
  };

  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allVisibleSelected = filteredRows.length > 0 && filteredRows.every((r) => selected.has(r.campaign_id));
  const toggleAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) filteredRows.forEach((r) => next.delete(r.campaign_id));
      else filteredRows.forEach((r) => next.add(r.campaign_id));
      return next;
    });
  };

  const addPlaceholder = (p) => setDraft((d) => ({ ...d, body_values: [...d.body_values, p] }));
  const setBodyValue = (i, val) =>
    setDraft((d) => ({ ...d, body_values: d.body_values.map((v, idx) => (idx === i ? val : v)) }));
  const addBodyValue = () => setDraft((d) => ({ ...d, body_values: [...d.body_values, ''] }));
  const removeBodyValue = (i) =>
    setDraft((d) => ({ ...d, body_values: d.body_values.filter((_, idx) => idx !== i) }));

  const handleApply = async () => {
    if (selected.size === 0) { toast.error('Select at least one event to apply to.'); return; }
    if (draft.is_active && !isCommon && !draft.template_name.trim()) {
      toast.error('Enter the approved WhatsApp template name to turn this on.');
      return;
    }
    setApplying(true);
    try {
      const res = await api.put('/whatsapp-templates/bulk-apply', {
        trigger,
        template_name: isCommon ? '' : draft.template_name.trim(),
        body_values: isCommon ? [] : draft.body_values.filter((v) => v !== null && v !== undefined),
        language_code: draft.language_code.trim() || 'en',
        country_code: (draft.country_code || '').replace(/\D/g, '') || '91',
        is_active: draft.is_active,
        campaign_ids: Array.from(selected),
      });
      const { updated_count, failed_count, failed } = res.data;
      if (failed_count > 0) {
        toast.error(`Applied to ${updated_count} event(s), ${failed_count} failed — see console for details.`);
        // eslint-disable-next-line no-console
        console.warn('WhatsApp bulk-apply failures:', failed);
      } else {
        toast.success(`Applied "${labelForTrigger(trigger)}" to ${updated_count} event(s).`);
      }
      await fetchOverview();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply');
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-5xl mx-auto">
        <button
          onClick={() => navigate('/control-panel/integrations')}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-4"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Integrations
        </button>

        <div className="mb-6 flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center shrink-0">
            <MessageCircle className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h1 className="text-2xl font-outfit font-bold text-slate-900">WhatsApp Templates — All Events</h1>
            <p className="text-sm text-slate-600 mt-0.5">
              Configure one WhatsApp message once and push it to every event, instead of setting each one up
              individually in its own Settings → WhatsApp tab.
            </p>
          </div>
        </div>

        {!configured && (
          <div className="mb-5 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
            WhatsApp (Interakt) isn't configured on the server, so messages won't send even when turned on.
          </div>
        )}

        {/* Trigger picker */}
        <Card className="p-4 mb-5">
          <Label className="text-xs text-slate-600 mb-2 block">Which message</Label>
          <div className="flex flex-wrap gap-2">
            {ALL_TRIGGER_KEYS.map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => handleTriggerChange(key)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                  trigger === key
                    ? 'bg-primary text-white border-primary'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-primary/50'
                }`}
              >
                {labelForTrigger(key)}
              </button>
            ))}
          </div>
        </Card>

        {/* Editor for the picked trigger */}
        <Card className="p-6 mb-5 space-y-4">
          {isCommon ? (
            <>
              <div>
                <Label className="text-xs text-slate-600">Message</Label>
                <pre className="mt-1 p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-700 whitespace-pre-wrap font-sans">
                  {COMMON_TEMPLATE_INFO[trigger].lines.join('\n')}
                </pre>
              </div>
              <div>
                <Label className="text-xs text-slate-600">Approved template name</Label>
                <div className="mt-1 h-10 px-3 flex items-center rounded-md border border-slate-200 bg-slate-50 text-sm font-mono text-slate-700 truncate">
                  {commonEffectiveName || '—'}
                </div>
                <p className={`text-[11px] mt-1 ${commonEffectiveName ? 'text-slate-500' : 'text-amber-600'}`}>
                  {commonEffectiveName
                    ? 'Set on the server and shared by every event.'
                    : 'Not set on the server yet — this message cannot be turned on until it is.'}
                </p>
              </div>
              <div>
                <Label className="text-xs text-slate-600">Variables (fixed)</Label>
                <div className="mt-1 space-y-1.5">
                  {COMMON_TEMPLATE_INFO[trigger].variables.map((v) => (
                    <div key={v.slot} className="flex items-center gap-2 text-xs">
                      <span className="font-mono text-slate-400 w-9 shrink-0">{v.slot}</span>
                      <span className="font-mono px-2 py-0.5 rounded bg-slate-100 text-slate-600">{v.value}</span>
                      <span className="text-slate-500">{v.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-1">
                  <Label className="text-xs text-slate-600">Approved template name</Label>
                  <Input
                    value={draft.template_name}
                    onChange={(e) => setDraft({ ...draft, template_name: e.target.value })}
                    placeholder="e.g. registration_confirmed"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Language code</Label>
                  <Input
                    value={draft.language_code}
                    onChange={(e) => setDraft({ ...draft, language_code: e.target.value })}
                    placeholder="en"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Dialing code</Label>
                  <Input
                    value={draft.country_code}
                    onChange={(e) => setDraft({ ...draft, country_code: e.target.value })}
                    placeholder="91"
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-slate-600">Template variables (in order)</Label>
                  <Button variant="ghost" size="sm" onClick={addBodyValue} className="h-7 text-primary">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add variable
                  </Button>
                </div>
                <div className="space-y-2 mt-1">
                  {draft.body_values.length === 0 && (
                    <p className="text-xs text-slate-400">No variables — the template will send with no dynamic values.</p>
                  )}
                  {draft.body_values.map((v, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-xs font-mono text-slate-400 w-9 shrink-0">{`{{${i + 1}}}`}</span>
                      <Input
                        value={v}
                        onChange={(e) => setBodyValue(i, e.target.value)}
                        placeholder="${name}"
                        className="font-mono text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => removeBodyValue(i)}
                        className="p-1.5 text-slate-400 hover:text-red-500"
                        title="Remove"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="mt-3">
                  <p className="text-[11px] text-slate-500 mb-1">Event &amp; payment values</p>
                  <div className="flex flex-wrap gap-1.5">
                    {SYSTEM_PLACEHOLDERS.map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => addPlaceholder(p)}
                        className="text-[11px] font-mono px-2 py-0.5 rounded bg-slate-100 text-slate-600 hover:bg-primary/10 hover:text-primary"
                        title="Add as a variable"
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                  <p className="text-[11px] text-slate-500 mt-2 flex items-start gap-1.5">
                    <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    You can also type <code className="font-mono">{'${field:Label}'}</code> for a form field —
                    it resolves per event and sends blank for any event whose form doesn't have that field.
                  </p>
                </div>
              </div>
            </>
          )}

          <div className="flex items-center justify-between pt-3 border-t border-slate-100">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <Switch checked={draft.is_active} onCheckedChange={(v) => setDraft({ ...draft, is_active: v })} />
              Send this message
            </label>
          </div>
        </Card>

        {/* Target events */}
        <Card className="p-4 mb-5">
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <div>
              <Label className="text-xs text-slate-600">Apply to</Label>
              <p className="text-[11px] text-slate-500 mt-0.5">
                Every event is selected by default. Uncheck any you want to leave untouched.
              </p>
            </div>
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search events…"
                className="pl-8 h-9 w-56"
              />
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto border border-slate-200 rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox checked={allVisibleSelected} onCheckedChange={toggleAllVisible} />
                  </TableHead>
                  <TableHead>Event</TableHead>
                  <TableHead>This message</TableHead>
                  <TableHead>WhatsApp for event</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-sm text-slate-400 py-6">
                      No events match "{search}".
                    </TableCell>
                  </TableRow>
                )}
                {filteredRows.map((r) => (
                  <TableRow key={r.campaign_id}>
                    <TableCell>
                      <Checkbox
                        checked={selected.has(r.campaign_id)}
                        onCheckedChange={() => toggleRow(r.campaign_id)}
                      />
                    </TableCell>
                    <TableCell className="text-sm text-slate-800">{r.campaign_name}</TableCell>
                    <TableCell>
                      {r.current?.is_active ? (
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">On</span>
                      ) : (
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 font-medium">Off</span>
                      )}
                      {r.current?.effective_template_name && (
                        <span className="ml-2 text-[11px] text-slate-400 font-mono">{r.current.effective_template_name}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {r.whatsapp_enabled ? (
                        <span className="text-xs text-slate-500">On</span>
                      ) : (
                        <span className="text-xs text-amber-600 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> Off — nothing sends here yet
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>

        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={fetchOverview}>
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button onClick={handleApply} disabled={applying} className="bg-primary text-white">
            {applying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Apply to {selected.size} event{selected.size === 1 ? '' : 's'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppTemplatesManager;
